import { useCallback, useEffect, useState } from "react";
import Seo from "@/components/base/Seo";
import AdminPageHeader from "@/pages/admin/components/AdminPageHeader";
import { useAuth } from "@/context/AuthContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import {
  getAdminMarquee,
  saveAdminMarqueeMessage,
  deleteAdminMarqueeMessage,
} from "@/services/adminApi";
import { MARQUEE_COLORS, marqueeColor } from "@/config/marqueeColors";
import type { MarqueeMessage } from "@/types";

const MAX_MESSAGES = 5;

/** Scroll-speed presets — each value is the loop duration in seconds (higher = slower). */
const SPEED_PRESETS = [
  { label: "Very slow", seconds: 80 },
  { label: "Slow", seconds: 52 },
  { label: "Normal", seconds: 32 },
  { label: "Fast", seconds: 20 },
  { label: "Very fast", seconds: 12 },
];

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";
const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500";

interface FormState {
  message: string;
  linkUrl: string;
  linkLabel: string;
  color: string;
  isActive: boolean;
}

const EMPTY_FORM: FormState = { message: "", linkUrl: "", linkLabel: "", color: "default", isActive: true };

export default function AdminAnnouncements() {
  const { token } = useAuth();
  const { settings, save } = useSiteSettings();
  const [speedSaving, setSpeedSaving] = useState(false);
  const [rows, setRows] = useState<MarqueeMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<MarqueeMessage | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2400);
  };

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    getAdminMarquee(token)
      .then((list) => {
        setRows(list.map((m) => ({ ...m })));
      })
      .catch((err: unknown) => {
        setRows([]);
        setError(err instanceof Error ? err.message : "Could not load the messages. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (part: Partial<FormState>) => setForm((f) => ({ ...f, ...part }));

  const startAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, isActive: true });
    setError("");
  };

  const startEdit = (row: MarqueeMessage) => {
    setEditing(row);
    setForm({ message: row.message, linkUrl: row.linkUrl, linkLabel: row.linkLabel, color: row.color || "default", isActive: row.isActive });
    setError("");
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const message = form.message.trim();
    if (!message) {
      setError("Please type the announcement text.");
      return;
    }
    setSaving(true);
    setError("");
    const displayOrder = editing?.displayOrder ?? rows.length + 1;
    try {
      await saveAdminMarqueeMessage(
        {
          id: editing?.id,
          message,
          linkUrl: form.linkUrl.trim(),
          linkLabel: form.linkLabel.trim(),
          color: form.color,
          displayOrder,
          isActive: form.isActive,
        },
        token,
      );
      if (editing) {
        setRows((prev) =>
          prev.map((r) =>
            r.id === editing.id
              ? { ...r, message, linkUrl: form.linkUrl.trim(), linkLabel: form.linkLabel.trim(), color: form.color, isActive: form.isActive }
              : r,
          ),
        );
      } else {
        setRows((prev) => [
          ...prev,
          {
            id: `local-${Date.now()}`,
            message,
            linkUrl: form.linkUrl.trim(),
            linkLabel: form.linkLabel.trim(),
            color: form.color,
            displayOrder,
            isActive: form.isActive,
          },
        ]);
      }
      setEditing(null);
      setForm(EMPTY_FORM);
      flash(editing ? "Message updated." : "Message added.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the message. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row: MarqueeMessage) => {
    const next = !row.isActive;
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, isActive: next } : r)));
    try {
      await saveAdminMarqueeMessage(
        {
          id: row.id,
          message: row.message,
          linkUrl: row.linkUrl,
          linkLabel: row.linkLabel,
          color: row.color,
          displayOrder: row.displayOrder,
          isActive: next,
        },
        token,
      );
    } catch {
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, isActive: row.isActive } : r)));
      setError("Could not update the message. Please try again.");
    }
  };

  const remove = async (row: MarqueeMessage) => {
    try {
      const okDelete = await deleteAdminMarqueeMessage(row.id, token);
      if (!okDelete) throw new Error("failed");
    } catch {
      setError("Could not remove the message. Please try again.");
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== row.id));
    if (editing?.id === row.id) {
      setEditing(null);
      setForm(EMPTY_FORM);
    }
    flash("Message removed.");
  };

  const currentSpeed = Number(settings?.marqueeSpeed) || 32;

  const changeSpeed = async (seconds: number) => {
    if (seconds === currentSpeed) return;
    setSpeedSaving(true);
    setError("");
    try {
      await save({ marqueeSpeed: seconds });
      flash("Scroll speed updated.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the scroll speed. Please try again.");
    } finally {
      setSpeedSaving(false);
    }
  };

  const atCapacity = rows.length >= MAX_MESSAGES && !editing;

  return (
    <>
      <Seo title="Marquee Messages" path="/admin/announcements" />

      <AdminPageHeader
        icon="ri-notification-3-line"
        title="Marquee messages"
        subtitle="The scrolling announcement ticker shown at the top of every public page."
        actions={
          notice ? (
            <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
              <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
            </span>
          ) : undefined
        }
      />

      {error && (
        <div className="mt-4 flex items-start gap-3 rounded-md border border-secondary-200 bg-secondary-50 p-3.5 text-sm text-secondary-900">
          <i className="ri-error-warning-line mt-0.5 text-lg text-secondary-600"></i>
          <div className="flex-1">
            <p>{error}</p>
            {rows.length === 0 && (
              <button
                type="button"
                onClick={load}
                className="mt-2 inline-flex items-center gap-2 rounded-md border border-secondary-300 px-3 py-1.5 text-xs font-semibold text-secondary-900 hover:bg-secondary-100 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line"></i> Try again
              </button>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-foreground-500">
              {rows.length} of {MAX_MESSAGES} messages used
            </p>
            <button
              type="button"
              onClick={startAdd}
              disabled={atCapacity}
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line"></i> Add message
            </button>
          </div>

          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-24 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 p-8 text-center text-sm text-foreground-500">
              No messages yet. Add up to {MAX_MESSAGES} announcements to show across the site.
            </div>
          ) : (
            rows.map((row, index) => (
              <article
                key={row.id}
                className={`rounded-lg border bg-background-50 p-4 ${
                  editing?.id === row.id ? "border-primary-300" : "border-background-200"
                }`}
              >
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-100 text-sm font-bold text-primary-800">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 text-sm font-medium text-foreground-900">
                      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${marqueeColor(row.color).dot}`}></span>
                      {row.message}
                    </p>
                    {row.linkUrl && (
                      <p className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
                        <i className="ri-link"></i>
                        <span className="truncate">{row.linkUrl}</span>
                        {row.linkLabel && (
                          <span className="rounded-full bg-secondary-100 px-2 py-0.5 font-semibold text-secondary-900">
                            {row.linkLabel}
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                  <span
                    className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold ${
                      row.isActive ? "bg-accent-100 text-accent-900" : "bg-background-200 text-foreground-600"
                    }`}
                  >
                    {row.isActive ? "Active" : "Hidden"}
                  </span>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-background-100 pt-3">
                  <button
                    type="button"
                    onClick={() => toggleActive(row)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-xs font-semibold text-foreground-700 hover:border-primary-400 whitespace-nowrap cursor-pointer"
                  >
                    <i className={row.isActive ? "ri-eye-off-line" : "ri-eye-line"}></i>
                    {row.isActive ? "Hide" : "Show"}
                  </button>
                  <button
                    type="button"
                    onClick={() => startEdit(row)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-xs font-semibold text-foreground-700 hover:border-primary-400 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-pencil-line"></i> Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(row)}
                    className="ml-auto inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold text-secondary-700 hover:bg-secondary-50 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i> Remove
                  </button>
                </div>
              </article>
            ))
          )}
        </div>

        <aside className="space-y-5">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">Scroll speed</h2>
            <p className="mt-1 text-xs text-foreground-500">
              How fast the ticker moves across the bar. This applies everywhere the announcement bar shows.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {SPEED_PRESETS.map((preset) => {
                const isActive = preset.seconds === currentSpeed;
                return (
                  <button
                    key={preset.seconds}
                    type="button"
                    disabled={speedSaving}
                    onClick={() => changeSpeed(preset.seconds)}
                    className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer ${
                      isActive
                        ? "border-primary-400 bg-primary-100 text-primary-800"
                        : "border-background-300 text-foreground-700 hover:border-primary-400"
                    }`}
                  >
                    {isActive && <i className="ri-check-line"></i>}
                    {preset.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-3 flex items-center gap-2 text-xs text-foreground-500">
              <i className="ri-timer-line"></i>
              {speedSaving ? "Saving…" : `One full loop takes about ${currentSpeed} seconds.`}
            </p>
          </div>

          <form onSubmit={submit} className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">
              {editing ? "Edit message" : "New message"}
            </h2>
            <p className="mt-1 text-xs text-foreground-500">
              Keep it short and clear — it scrolls across the top of every page.
            </p>

            <div className="mt-4 space-y-4">
              <div>
                <label htmlFor="marquee-message" className={labelClass}>Message</label>
                <textarea
                  id="marquee-message"
                  name="message"
                  rows={3}
                  maxLength={255}
                  value={form.message}
                  onChange={(e) => patch({ message: e.target.value })}
                  placeholder="e.g. Festive offer: 20% off the Business plan this month."
                  className={`${inputClass} resize-none`}
                ></textarea>
              </div>
              <div>
                <label htmlFor="marquee-link" className={labelClass}>Link (optional)</label>
                <input
                  id="marquee-link"
                  name="linkUrl"
                  value={form.linkUrl}
                  onChange={(e) => patch({ linkUrl: e.target.value })}
                  placeholder="/listing-plans or https://…"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="marquee-link-label" className={labelClass}>Link label (optional)</label>
                <input
                  id="marquee-link-label"
                  name="linkLabel"
                  value={form.linkLabel}
                  onChange={(e) => patch({ linkLabel: e.target.value })}
                  placeholder="e.g. See plans"
                  className={inputClass}
                />
              </div>
              <div>
                <span className={labelClass}>Message colour</span>
                <div className="mt-1 flex flex-wrap gap-2">
                  {MARQUEE_COLORS.map((option) => {
                    const selected = form.color === option.key;
                    return (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => patch({ color: option.key })}
                        aria-pressed={selected}
                        className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md border px-2.5 py-1.5 text-xs font-semibold transition-colors cursor-pointer ${
                          selected
                            ? "border-primary-400 bg-primary-50 text-primary-800"
                            : "border-background-300 text-foreground-700 hover:border-primary-300"
                        }`}
                      >
                        <span className={`h-3 w-3 rounded-full ${option.dot}`}></span>
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <label className="flex cursor-pointer items-center justify-between gap-3 border-t border-background-100 pt-4">
                <span className="text-sm font-semibold text-foreground-900">Show in the ticker</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.isActive}
                  onClick={() => patch({ isActive: !form.isActive })}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer ${
                    form.isActive ? "bg-primary-500" : "bg-background-300"
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-background-50 transition-transform ${
                      form.isActive ? "translate-x-5" : "translate-x-0.5"
                    }`}
                  ></span>
                </button>
              </label>
            </div>

            <div className="mt-5 flex gap-2">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-foreground-950 hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
                {editing ? "Save changes" : "Add message"}
              </button>
              {editing && (
                <button
                  type="button"
                  onClick={startAdd}
                  className="inline-flex items-center justify-center gap-1.5 rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-700 hover:border-primary-400 whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </aside>
      </div>
    </>
  );
}